<template>
  <div>
    <DxPopup :visible="visible" :show-title="true" :title="$t('works.work_list.edit_work')" :width="800" :height="520" @hiding="onClose">
      <form>
        <div class="row">
          <div class="form-row col-sm-12 col-lg-6">
            <label class="control-label"> {{ $t("common.date") }}:</label>
            <DxDateBox v-model="work_started_at" display-format="yyyy-MM-dd" :class="{ 'field-error': this.$v.work_started_at.$error }" />
          </div>
          <div class="form-row col-sm-12 col-lg-6">
            <label class="control-label">{{ $t("common.work_type") }}:</label>
            <DxSelectBox
              :search-enabled="true"
              v-model="work_uuid"
              :data-source="workTypes.data"
              value-expr="uuid"
              display-expr="name"
              :class="{ 'field-error': this.$v.work_uuid.$error }"
            />
          </div>
        </div>
        <div class="row">
          <div class="form-row col-sm-12 col-lg-6">
            <label class="control-label">{{ $t("common.tractor") }}:</label>
            <DxSelectBox
              :search-enabled="true"
              v-model="machine_uuid"
              :data-source="workMachineList"
              value-expr="uuid"
              display-expr="name"
              :class="{ 'field-error': this.$v.machine_uuid.$error }"
            />
          </div>
          <div class="form-row col-sm-12 col-lg-6">
            <label class="control-label">{{ $t("common.work_machine") }}:</label>
            <DxSelectBox
              :search-enabled="true"
              v-model="machinery_uuid"
              :data-source="equipmentList"
              value-expr="uuid"
              display-expr="name"
            />
          </div>
        </div>
        <div class="row">
          <div class="form-row col-sm-12 col-lg-6">
            <label>{{ $t("common.parcel") }}:</label>
            <div class="sm-select-input-dialog-btn" @click="parcelClick" :class="{ 'field-error': this.$v.selectedParcels.$error }">
              <div class="sm-select-input-dialog-info">
                <div class="sm-select-input-dialog-value">{{ getParcelNames }}</div>
              </div>
              <div class="sm-select-input-dialog-icon">
                <i class="fa-regular fa-edit"></i>
              </div>
            </div>
          </div>
          <div class="form-row col-sm-12 col-lg-6">
            <label class="control-label">{{ $t("works.work_list.worked_area") }}:</label>
            <DxNumberBox v-model="net_surface_worked" format="##0.## ha" :class="{ 'field-error': this.$v.net_surface_worked.$error }" />
          </div>
        </div>
        <div class="row">
          <div class="form-row col-sm-12 col-lg-6">
            <label class="control-label">{{ $t("common.driver") }}:</label>
            <DxSelectBox :search-enabled="true" v-model="person_uuid" :data-source="personList" value-expr="uuid" display-expr="name" />
          </div>
          <div class="form-row col-sm-12 col-lg-6">
            <label class="control-label">{{ $t("remark") }}:</label>
            <DxTextArea v-model="notes" />
          </div>
        </div>
        <div class="row">
          <div class="text-right">
            <SmagriaButton :label="$t('ok')" :action="saveWork" :customClass="'primary margin-top'" :width="'170px'" />
            <SmagriaButton :label="$t('common.cancel')" :action="onClose" :customClass="'secondary border margin-left margin-top'" :width="'130px'" />
          </div>
        </div>
      </form>
    </DxPopup>
    <ParcelSelector
      ref="parcelSelector"
      :visible="showParcelSelectDialog"
      :selectedParcelUuids="selectedParcels.map((parcel) => parcel.uuid)"
      @close="showParcelSelectDialog = false"
      @continue="onContinueFromParcelSelector"
      :multiselect="false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ParcelSelector from "@/components/ParcelSelector.vue";
import DxPopup from "devextreme-vue/popup";
import DxDateBox from "devextreme-vue/date-box";
import DxSelectBox from "devextreme-vue/select-box";
import DxTextBox from "devextreme-vue/text-box";
import DxNumberBox from "devextreme-vue/number-box";
import DxTextArea from "devextreme-vue/text-area";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import { successMessage, errorMessage } from "@/helpers/common";
import required from "vuelidate/src/validators/required";

export default {
  name: "WorkEdit",
  props: {
    workData: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  components: {
    ParcelSelector,
    DxPopup,
    DxDateBox,
    DxSelectBox,
    DxTextBox,
    DxNumberBox,
    DxTextArea,
    SmagriaButton,
  },
  data() {
    return {
      work_started_at: this.workData.work_started_at,
      work_uuid: this.workData.work.uuid,
      machine_uuid: this.workData.machine ? this.workData.machine.uuid : null,
      machinery_uuid: this.workData.machinery ? this.workData.machinery.uuid : null,
      person_uuid: this.workData.person ? this.workData.person?.uuid : null,
      net_surface_worked: parseFloat(this.workData.net_surface_worked),
      notes: this.workData.notes,
      workMachineList: [],
      equipmentList: [],
      personList: [],
      showParcelSelectDialog: false,
      selectedParcels: [this.workData.parcel],
    };
  },
  watch: {},
  computed: {
    ...mapGetters({
      workTypes: "works-manager/works",
    }),
    getParcelNames() {
      return this.selectedParcels.map((l) => l.name).join(", ");
    },
  },
  methods: {
    ...mapActions({
      changeWork: "seasonOnParcelHasCulture/updateWork",
      getWorkMachineList: "works-work-machines/getWorkMachines",
      getWorkMachineEquipmentsList: "works-work-machines/getWorkMachineEquipments",
      getPersons: "works-persons/getPersons",
    }),
    onClose() {
      this.$emit("close");
    },
    saveWork() {
      this.$v.$touch(); // Trigger validation
      if (this.$v.$invalid) {
        errorMessage(this, this.$t("fill_out_error"));
        return;
      }

      /*if (!this.work_started_at || !this.work_uuid || !this.machine_uuid || !this.net_surface_worked || !this.selectedParcels.length) {
        errorMessage(this, this.$t("fill_out_error"));
        return;
      }*/

      this.update();
    },
    onContinueFromParcelSelector(selectedParcels) {
      this.selectedParcels = selectedParcels; // Update localData with selected parcels
      this.showParcelSelectDialog = false; // Close the ParcelSelector dialog
    },
    parcelClick() {
      this.showParcelSelectDialog = true; // Show the ParcelSelector dialog
    },
    async update(key, obj) {
      let updatable = {};
      updatable = {
        uuid: this.workData.uuid,
        machines: [],
      };

      let updateNeeded = false;
      if (this.work_started_at !== this.workData.work_started_at) {
        updatable.work_started_at = this.work_started_at;
        updateNeeded = true;
      }
      if (this.work_uuid !== this.workData.work.uuid) {
        updatable.work_uuid = this.work_uuid;
        updateNeeded = true;
      }
      if (this.person_uuid !== (this.workData.person ? this.workData.person?.uuid : null)) {
        updatable.persons_uuid = this.person_uuid;
        updateNeeded = true;
      }
      if (this.net_surface_worked !== parseFloat(this.workData.net_surface_worked)) {
        updatable.net_surface_worked = this.net_surface_worked;
        updateNeeded = true;
      }
      if (this.notes !== this.workData.notes) {
        updatable.notes = this.notes;
        updateNeeded = true;
      }
      if (this.selectedParcels.length > 0 && this.selectedParcels[0].uuid !== this.workData.parcel.uuid) {
        updatable.season_on_parcel_uuid = this.selectedParcels.map((parcel) => parcel.season_on_parcel_uuid)[0] || null;
        updatable.department_uuid = this.selectedParcels.map((parcel) => parcel.department_uuid)[0] || null;
        updateNeeded = true;
      }
      if (
        this.machine_uuid !== (this.workData.machine ? this.workData.machine?.uuid : null) ||
        this.machinery_uuid !== (this.workData.machinery ? this.workData.machinery?.uuid : null) ||
        updateNeeded
      ) {
        updatable.machine_uuid = this.machine_uuid;
        if (this.machine_uuid) {
          updatable.machines.push({ uuid: this.machine_uuid });
        }
        if (this.machinery_uuid) {
          updatable.machines.push({ uuid: this.machinery_uuid });
        }
        updateNeeded = true;
      }

      if (!updateNeeded) {
        // No changes detected
        this.onClose();
        return;
      }

      // Call the update API
      await this.changeWork(updatable)
        .then((response) => {
          successMessage(this, "Work has been updated.");
          this.$emit("save");
        })
        .catch((e) => {
          errorMessage(this, "Work update error.");
        });
    },
  },
  mounted() {
    this.getWorkMachineList({
      limit: 10000,
    }).then((response) => {
      response.data.unshift({ uuid: null, name: "" });
      this.workMachineList = response.data;
    });
    this.getWorkMachineEquipmentsList({ limit: 10000 }).then((response) => {
      response.unshift({ uuid: null, name: "" });
      this.equipmentList = response;
    });

    //const department_uuid = this.workData?.machine?.department_uuid ?? this.workData?.parcel?.department_uuid;
    this.getPersons({ limit: 10000 /*, department_uuid*/ }).then((response) => {
      response.data.unshift({ uuid: null, name: "" });
      this.personList = response.data;
    });
  },
  validations() {
    return {
      work_started_at: { required },
      work_uuid: { required },
      machine_uuid: { required },
      net_surface_worked: {
        required: (value) => !!value,
        minValue: (value) => value > 0,
      },
      selectedParcels: {
        required: (value) => value.length > 0,
      },
    };
  },
};
</script>

<style scoped>
.form-row {
  align-items: center;
  margin-bottom: 20px;
}

.control-label {
  padding-bottom: 5px;
}
</style>
